// Minimal WASI preview1 for a CLAP plugin module: console output, clocks, random bytes, no filesystem
import { isDefined } from "@opendaw/lib-std";
import { decodeUtf8 } from "../utf8";
const ERRNO_SUCCESS = 0;
const ERRNO_BADF = 8;
const ERRNO_NOENT = 44;
const ERRNO_NOSYS = 52;
const FILETYPE_CHARACTER_DEVICE = 2;
const CLOCK_MONOTONIC = 1;
export const createWasiImports = (module, memoryProvider, label, log) => {
    const view = () => new DataView(memoryProvider().buffer);
    const bytes = () => new Uint8Array(memoryProvider().buffer);
    const lines = ["", "", ""];
    const shim = {
        args_get: () => ERRNO_SUCCESS,
        args_sizes_get: (countPtr, sizePtr) => {
            view().setUint32(countPtr, 0, true);
            view().setUint32(sizePtr, 0, true);
            return ERRNO_SUCCESS;
        },
        environ_get: () => ERRNO_SUCCESS,
        environ_sizes_get: (countPtr, sizePtr) => {
            view().setUint32(countPtr, 0, true);
            view().setUint32(sizePtr, 0, true);
            return ERRNO_SUCCESS;
        },
        clock_time_get: (id, _precision, timePtr) => {
            const nanos = id === CLOCK_MONOTONIC && isDefined(globalThis.performance)
                ? BigInt(Math.round(performance.now() * 1_000_000))
                : BigInt(Date.now()) * 1000000n;
            view().setBigUint64(timePtr, nanos, true);
            return ERRNO_SUCCESS;
        },
        fd_close: () => ERRNO_BADF,
        fd_fdstat_get: (fd, statPtr) => {
            if (fd > 2) {
                return ERRNO_BADF;
            }
            bytes().fill(0, statPtr, statPtr + 24);
            view().setUint8(statPtr, FILETYPE_CHARACTER_DEVICE);
            return ERRNO_SUCCESS;
        },
        fd_fdstat_set_flags: () => ERRNO_BADF,
        fd_prestat_get: () => ERRNO_BADF,
        fd_prestat_dir_name: () => ERRNO_BADF,
        fd_read: () => ERRNO_BADF,
        fd_seek: () => ERRNO_BADF,
        fd_write: (fd, iovsPtr, iovsCount, writtenPtr) => {
            const memory = bytes();
            const dataView = view();
            const chunks = [];
            const written = Array.from({ length: iovsCount }, (_, index) => {
                const bufferPtr = dataView.getUint32(iovsPtr + index * 8, true);
                const length = Math.min(dataView.getUint32(iovsPtr + index * 8 + 4, true), memory.length - bufferPtr);
                chunks.push(decodeUtf8(memory.subarray(bufferPtr, bufferPtr + length)));
                return length;
            }).reduce((sum, length) => sum + length, 0);
            dataView.setUint32(writtenPtr, written, true);
            if (fd === 1 || fd === 2) {
                const parts = (lines[fd] + chunks.join("")).split("\n");
                lines[fd] = parts.pop() ?? "";
                parts.forEach(log);
            }
            return ERRNO_SUCCESS;
        },
        path_open: () => ERRNO_NOENT,
        poll_oneoff: () => ERRNO_NOSYS,
        proc_exit: (code) => { throw new Error(`${label} exited with ${code}`); },
        random_get: (bufferPtr, length) => {
            const target = bytes().subarray(bufferPtr, bufferPtr + length);
            if (isDefined(globalThis.crypto)) {
                crypto.getRandomValues(target);
            }
            else {
                target.forEach((_, index) => target[index] = (Math.random() * 256) | 0);
            }
            return ERRNO_SUCCESS;
        },
        sched_yield: () => ERRNO_SUCCESS
    };
    const imports = {};
    for (const entry of WebAssembly.Module.imports(module)) {
        if (entry.kind !== "function" || entry.module === "env") {
            continue;
        }
        const namespace = imports[entry.module] ?? (imports[entry.module] = {});
        namespace[entry.name] = shim[entry.name] ?? (() => {
            log(`unsupported import ${entry.module}.${entry.name}`);
            return ERRNO_NOSYS;
        });
    }
    return imports;
};
// The declared limits of the module's memory import (none = the module exports its own memory)
export const readMemoryImportLimits = (bytes) => {
    const position = { index: 8 };
    const readLeb = () => {
        const state = { result: 0, shift: 0 };
        for (;;) {
            const byte = bytes[position.index++];
            state.result |= (byte & 0x7F) << state.shift;
            if ((byte & 0x80) === 0) {
                return state.result >>> 0;
            }
            state.shift += 7;
        }
    };
    const skipName = () => {
        const length = readLeb();
        position.index += length;
    };
    while (position.index < bytes.length) {
        const id = bytes[position.index++];
        const size = readLeb();
        const end = position.index + size;
        if (id === 2) {
            const count = readLeb();
            for (const _ of Array.from({ length: count })) {
                skipName();
                skipName();
                const kind = bytes[position.index++];
                if (kind === 0) {
                    readLeb();
                }
                else if (kind === 1) {
                    position.index++;
                    const flags = bytes[position.index++];
                    readLeb();
                    if ((flags & 1) !== 0) {
                        readLeb();
                    }
                }
                else if (kind === 2) {
                    const flags = bytes[position.index++];
                    const initial = readLeb();
                    const maximum = (flags & 1) !== 0 ? readLeb() : 32768;
                    return { initial, maximum, shared: (flags & 2) !== 0 };
                }
                else if (kind === 3) {
                    position.index += 2;
                }
            }
        }
        position.index = end;
    }
    return undefined;
};
